import { useEffect, useState } from 'react'

// Router
import { useNavigate, useSearchParams } from 'react-router-dom'

// Form
import { useForm } from 'react-hook-form'

// Services
import {
  useUpdateGuestRepInfo,
  useGetGuestAccessToken,
} from 'lib/services/api/getAccessToken'

// Types
import { GuestAccessFormData } from './GuestAccess.types'

const useGuestAccessLogic = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [error, setError] = useState<string | null>(null)

  const {
    control,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<GuestAccessFormData>({
    defaultValues: {
      email: '',
      confirmEmail: '',
    },
    mode: 'onChange',
  })

  const { mutateAsync: updateGuestRepInfo, isLoading: isUpdatingGuestRepInfo } =
    useUpdateGuestRepInfo()
  const {
    mutateAsync: getGuestAccessToken,
    isLoading: isGettingGuestAccessToken,
  } = useGetGuestAccessToken()

  const verificationToken = searchParams.get('token')
  const companyId = searchParams.get('companyId')
  const caseId = searchParams.get('caseId')
  const prevAuthResponse = localStorage.getItem('auth_response')
  const prevAccessToken = JSON.parse(prevAuthResponse || '{}').access_token

  useEffect(() => {
    if (!verificationToken) {
      setError('Missing verification token')
    } else if (!companyId) {
      setError('Missing company ID')
    }
  }, [verificationToken, companyId])

  useEffect(() => {
    if (prevAccessToken) {
      if (verificationToken && companyId) {
        updateGuestRepInfo(
          {
            userId: JSON.parse(prevAuthResponse || '{}').payload?.id,
            verificationToken,
          },
          {
            onSuccess: (response) => {
              localStorage.setItem('auth_response', JSON.stringify(response))
              localStorage.setItem(
                'guest_user',
                JSON.stringify(response.payload)
              )
              localStorage.setItem('verification_token', verificationToken)
              navigate(`/surgeries/${caseId}`, {
                state: {
                  canAddAsset: JSON.parse(prevAuthResponse || '{}').payload
                    ?.canAddAsset,
                  companyId: companyId,
                  isGuest: true,
                },
              })
            },
            onError: (error) => {
              console.error('Error updating guest rep info:', error)
              navigate('/home', { replace: true })
            },
          }
        )
      }
    }
  }, [
    prevAccessToken,
    verificationToken,
    updateGuestRepInfo,
    navigate,
    companyId,
    prevAuthResponse,
    caseId,
  ])

  const handleGuestAccess = async (data: GuestAccessFormData) => {
    if (!verificationToken || !companyId) {
      return
    }

    try {
      // Call the API to get a guest access token
      const response = await getGuestAccessToken({
        verificationToken,
        companyId,
        caseId,
        email: data.email,
      })

      // Store the token in localStorage
      localStorage.setItem('auth_response', JSON.stringify(response))
      localStorage.setItem('guest_user', JSON.stringify(response.payload))
      localStorage.setItem('verification_token', verificationToken)

      // Navigate to the main application
      navigate(`/surgeries/${caseId}`, {
        state: {
          canAddAsset: response.payload?.canAddAsset,
          companyId: response.payload?.companyId,
          isGuest: true,
        },
      })
    } catch (err) {
      if (err instanceof Error) {
        console.error('Error obtaining guest access:', err)
        setError(err.message || 'Failed to authenticate as guest')
      }
    }
  }

  return {
    error,
    errors,
    caseId,
    control,
    companyId,
    isLoading: isGettingGuestAccessToken,
    isUpdatingGuestRepInfo,
    verificationToken,
    getValues,
    handleSubmit,
    handleGuestAccess,
  }
}

export default useGuestAccessLogic
